import React, { useState, useCallback, useEffect, useMemo } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Typography, Box, Paper, Stack, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, BoxStyle, BackArrowStyle } from "services/constants";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { ESIReportTable } from "./ESIReportTable";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { ArrowBackIos } from "@mui/icons-material";
import { FilterSection } from "pages/FilterContext/FilterRow";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { ESIReportKeys } from "pages/PayRoll/PayRollHeaders";

export const ESIReport = ({ setShowReport }) => {
  const {
    domain,
    token,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [companyId, setCompanyId] = useState(0);
  const [ESIDetails, setESIDetails] = useState([]);
  const [paySheduleDetails, setPayScheduleDetails] = useState(true);
  const [getEsiReportApi, setGetEsiReportApi] = useState(false);
  const [getPayScheduleDetails, setGetPayScheduleDetails] = useState([]);
  const [payScheduleName, setPayScheduleName] = useState("");
  const [listViewPage, setListViewPage] = useState(0);
  const [empESIData, setEmpESIData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loadingPayScheduleDetails, setLoadingPayScheduleDetails] =
    useState(false);
  const {
    result: payShedule,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    companyId !== 0
      ? `${domain}payschedule-report/?company=${companyId}&company_id=${topbarCompanyId}`
      : null,
  );

  const payrollReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  const subModuleId = payrollReports?.submodules?.find(
    (sub) => sub.sub_module_name === "ESI Report",
  )?.sub_module_id;

  // Read-only report -- filter/sort/download/save-view are view-level actions,
  // not edit actions. Gate on viewer permission (see EPFReport/index.js for
  // the fuller reasoning); editor is always false for Reports submodules in
  // the live payload and was blocking these actions for every user.
  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "ESI Report",
  );

  const handleSavedViewChange = useCallback(() => {
    setListViewPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "esi_report_saved_views",
    fullHeaders: ESIReportKeys,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  useEffect(() => {
    if (topbarCompanyId) {
      setCompanyId(topbarCompanyId);
      reCallApi(true);
      setTimeout(function () {
        setPayScheduleDetails(true);
      }, 300);
    }
  }, [reCallApi, topbarCompanyId]);

  useEffect(() => {
    if (payShedule.length !== 0 && paySheduleDetails && !loading) {
      setGetPayScheduleDetails(payShedule[0] ? payShedule[0] : []);
      setPayScheduleName(payShedule[0] ? payShedule[0]?.name : "");
      setPayScheduleDetails(false);
      if (payShedule[0]) {
        setLoadingPayScheduleDetails(true);
        setGetEsiReportApi(true);
      }
    } else if (payShedule.length === 0) {
      setGetPayScheduleDetails([]);
      setPayScheduleName("");
      setESIDetails([]);
    }
  }, [loading, payShedule, paySheduleDetails]);

  useEffect(() => {
    const getESIReports = async () => {
      const res = await customGetAPI(
        `${domain}pay-schedule/detailed/esi-report/?pay_schedule_id=${getPayScheduleDetails?.id}&company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setESIDetails(res?.responseDetails?.report_data);
        setLoadingPayScheduleDetails(false);
      } else {
        setESIDetails([]);
        setLoadingPayScheduleDetails(false);
      }
    };
    if (getEsiReportApi) {
      setGetEsiReportApi(false);
      getESIReports();
    }
  }, [domain, getEsiReportApi, getPayScheduleDetails?.id, token, topbarCompanyId]);

  const handlePaySchedule = (event, data) => {
    if (data) {
      setGetPayScheduleDetails(data);
      setPayScheduleName(data.name);
      setGetEsiReportApi(true);
      setLoadingPayScheduleDetails(true);
    }
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setEmpESIData(filteredData);
      if (listViewPage * rowsPerPage >= filteredData.length) setListViewPage(0);
    },
    [listViewPage, rowsPerPage],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Pay Schedule Name": payScheduleName || "N/A",
      "Report Generated On": new Date().toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    }),
    [payScheduleName, topbarCompanyName],
  );

  return (
    <ThemeProvider theme={theme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={BoxStyle}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <IconButton
              onClick={() => setShowReport(false)}
              sx={BackArrowStyle}
            >
              <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
            </IconButton>

            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}
              >
                Employees' State Insurance (ESI)
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Employees' State Insurance (ESI) Report provides a detailed
                overview of the contributions made by both employees and
                employers towards the ESI scheme.
              </Typography>
            </Box>
          </Stack>

          <FilterSection
            filterLabel="Filters"
            filters={[
              {
                width: 480,
                props: {
                  dataList: payShedule.map((obj) => ({
                    ...obj,
                    label: obj.name,
                  })),
                  selectedValue: payScheduleName,
                  updateDataFunction: handlePaySchedule,
                  Icons: PersonOutlineIcon,
                  staticLabel: "PaySchedule",
                },
              },
            ]}
          />
          <Paper sx={{ width: "100%", height: "76vh", mt: 2 }}>
            <CustomFilter
              title="ESI"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={ESIDetails}
              filteredData={empESIData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              newButtonComponent={newButtonComponent}
            />

            <Mui.Grid sx={{ ml: 2, mr: 2 }}>
              {loadingPayScheduleDetails ? (
                <Loader />
              ) : (
                <ESIReportTable
                  dataLength={ESIDetails}
                  ESIDetails={empESIData}
                  listViewPage={listViewPage}
                  setListViewPage={setListViewPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  headers={visibleHeaders}
                />
              )}
            </Mui.Grid>
          </Paper>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
